import { useTranslations } from '@vocab/react';
import {
  Text,
  TextLink,
  Hidden,
  Heading,
  Box,
  PageBlock,
} from 'braid-design-system';
import { throttle, debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fullWidthClassName } from 'react-remove-scroll-bar';

import { PROFILE_PAGE } from 'src/components/Router';
import { Link } from 'src/components/Router/Link';
import { SIXTY_FPS } from 'src/consts';
import { useAppConfig } from 'src/modules/appConfig';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';

import { CreateStep } from '../reducer';

import translations from './.vocab';
import { ProgressMarkerContainer } from './StepperContainer/StepperContainer';

import * as styles from './StickyHeader.css';

enum HeaderState {
  STICKY = 'STICKY',
  NORMAL = 'NORMAL',
}

const WINDOW_RESIZE_DEBOUNCE = 100;

export const StickyHeader = () => {
  const { brand } = useAppConfig();
  const { t } = useTranslations(translations);

  const createStep = useTypedSelector((state) => state.createResume.createStep);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [headerState, setHeaderState] = useState(HeaderState.NORMAL);
  const [headerHeight, setHeaderHeight] = useState<'auto' | number>(0);

  const onScroll = useCallback(() => {
    const rootOffset =
      (wrapperRef.current && wrapperRef.current.getBoundingClientRect().top) ||
      0;

    if (rootOffset <= 0 && createStep === CreateStep.PROFILE_REVIEW) {
      setHeaderState(HeaderState.STICKY);
    } else {
      setHeaderState(HeaderState.NORMAL);
    }
  }, [createStep]);

  const updateHeaderHeight = () => {
    setHeaderHeight('auto');
  };

  useEffect(() => {
    if (headerHeight === 'auto') {
      const height =
        (wrapperRef.current && wrapperRef?.current.offsetHeight) || 0;
      setHeaderHeight(height);
    }
  }, [headerHeight]);

  useEffect(() => {
    window.addEventListener('scroll', throttle(onScroll, SIXTY_FPS));
    window.addEventListener(
      'resize',
      debounce(updateHeaderHeight, WINDOW_RESIZE_DEBOUNCE),
    );

    updateHeaderHeight();
    onScroll();

    return () => {
      window.removeEventListener('scroll', throttle(onScroll, SIXTY_FPS));
      window.removeEventListener(
        'resize',
        debounce(updateHeaderHeight, WINDOW_RESIZE_DEBOUNCE),
      );
    };
  }, [onScroll]);

  useEffect(() => {
    if (createStep !== CreateStep.PROFILE_REVIEW) {
      updateHeaderHeight();
    }
  }, [createStep]);

  return (
    <div style={headerHeight ? { height: headerHeight } : {}} ref={wrapperRef}>
      <Box
        background="surface"
        boxShadow={headerState === HeaderState.STICKY ? 'small' : undefined}
        className={{
          [styles.root]: true,
          [styles.sticky]: headerState === HeaderState.STICKY,
          [fullWidthClassName]: true,
        }}
        data-automation="sticky-header"
      >
        <PageBlock width="medium">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flexStart"
            paddingY="large"
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="spaceBetween"
              width="full"
            >
              <Box marginRight="medium">
                <Heading level="3" component="h1">
                  {t('Create a resumé from your brand profile', {
                    Hidden: (v) => (
                      <Hidden below="tablet" inline>
                        {v}
                      </Hidden>
                    ),
                    brand,
                  })}
                </Heading>
              </Box>
              <Text align="right">
                <Link page={PROFILE_PAGE} component={TextLink}>
                  {t('Return to profile')}
                </Link>
              </Text>
            </Box>
            {createStep === CreateStep.PROFILE_REVIEW && (
              <Box
                paddingTop="large"
                display={['none', 'block']}
                className={styles.progress}
              >
                <ProgressMarkerContainer headerHeight={headerHeight} />
              </Box>
            )}
          </Box>
        </PageBlock>
      </Box>
    </div>
  );
};
